import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { UnitCurrencyActions } from '@app/+shared-state/unit-currency/unit-currency.actions';
import { of, switchMap } from 'rxjs';
import { CurrencyProvidersService, UnitsService } from '@curve-builder/shared/api/unit-currency/generated';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, filter, map } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { selectCurrencyProvidersFetchState } from '@app/+shared-state/unit-currency/unit-currency.selectors';

@Injectable()
export class UnitCurrencyEffects {
    private readonly store = inject(Store);
    private readonly actions = inject(Actions);

    private readonly currencies = inject(CurrencyProvidersService);
    private readonly units = inject(UnitsService);

    fetchCurrencyProviders$ = createEffect(() =>
        this.actions.pipe(
            ofType(UnitCurrencyActions.fetchCurrencyProviders),
            concatLatestFrom(() => this.store.select(selectCurrencyProvidersFetchState)),
            filter(([{ force }, fetchState]) => force || !fetchState?.loaded || fetchState?.loadingError),
            switchMap(() => {
                return this.currencies.getAllCurrencyProviders({}).pipe(
                    map(currencyProviders => {
                        return UnitCurrencyActions.fetchCurrencyProvidersSuccess({ currencyProviders });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(UnitCurrencyActions.fetchCurrencyProvidersError({ error }));
                    })
                );
            })
        )
    );

    fetchCurrencyProvidersForCurrency$ = createEffect(() =>
        this.actions.pipe(
            ofType(UnitCurrencyActions.fetchCurrencyProvidersForCurrency),
            switchMap(({ currency }) => {
                return this.currencies.getAllCurrencyProviders({ currency }).pipe(
                    map(currencyProviders => {
                        return UnitCurrencyActions.fetchCurrencyProvidersForCurrencySuccess({ currency, currencyProviders });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(UnitCurrencyActions.fetchCurrencyProvidersForCurrencyError({ currency, error }));
                    })
                );
            })
        )
    );

    fetchCurrenciesByProvider$ = createEffect(() =>
        this.actions.pipe(
            ofType(UnitCurrencyActions.fetchCurrenciesByProvider),
            filter(({ currencyProviderName }) => !!currencyProviderName),
            switchMap(({ currencyProviderName }) => {
                return this.currencies.getCurrenciesByProvider({ provider: currencyProviderName }).pipe(
                    map(currencies => {
                        return UnitCurrencyActions.fetchCurrenciesByProviderSuccess({ currencyProviderName, currencies });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(UnitCurrencyActions.fetchCurrenciesByProviderError({ currencyProviderName, error }));
                    })
                );
            })
        )
    );

    fetchAllISOCurrencies$ = createEffect(() =>
        this.actions.pipe(
            ofType(UnitCurrencyActions.fetchAllISOCurrencies),
            switchMap(() => {
                return this.currencies.getAllISOCurrencies().pipe(
                    map(isoCurrencies => {
                        return UnitCurrencyActions.fetchAllISOCurrenciesSuccess({ currencies: isoCurrencies });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(UnitCurrencyActions.fetchAllISOCurrenciesError({ error }));
                    })
                );
            })
        )
    );

    fetchUnits$ = createEffect(() =>
        this.actions.pipe(
            ofType(UnitCurrencyActions.fetchUnits),
            switchMap(() => {
                return this.units.getAllUnits().pipe(
                    map(units => {
                        return UnitCurrencyActions.fetchUnitsSuccess({ units });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(UnitCurrencyActions.fetchUnitsError({ error }));
                    })
                );
            })
        )
    );

    fetchSupportedUnits$ = createEffect(() =>
        this.actions.pipe(
            ofType(UnitCurrencyActions.fetchSupportedUnits),
            switchMap(({ originUnits }) => {
                return this.units.getSupportedUnits({ code: originUnits }).pipe(
                    map(units => {
                        return UnitCurrencyActions.fetchSupportedUnitsSuccess({ originUnits, units });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(UnitCurrencyActions.fetchSupportedUnitsError({ originUnits, error }));
                    })
                );
            })
        )
    );
}
